import { ChangeDetectionStrategy, Component, inject, input, output, viewChild, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { EventFormStore } from "../event.form.store";
import { S25EditableModule } from "../../../modules/s25-editable/s25.editable.module";
import { S25EditableStartEndDateTimeComponent } from "../../../modules/s25-editable/s25-editable-start-end-datetime/s25.editable.start.end.date.time.component";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { S25IconButtonComponent } from "../../s25.icon.button.component";
import { S25SimpleCollapseModule } from "../../../modules/s25-simple-collapse/s25.simple.collapse.module";
import { Bind } from "../../../decorators/bind.decorator";
import type { S25ItemI } from "../../../pojo/S25ItemI";
import type { DateOccurrenceRow } from "./event.form.date.occurrences.types";

@Component({
    selector: "tr[s25-ng-event-form-date-occurrence-row]",
    host: {
        class: "ngEventFormOcc",
        "[class.ng-event-form-occ--just-added]": "justAdded()",
        "[attr.data-occ-uuid]": "row().occ.uuid",
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, S25EditableModule, S25PipesModule, S25IconButtonComponent, S25SimpleCollapseModule],
    template: `
        @if (eventForm.model().hasSearch) {
            <td>
                <input
                    type="checkbox"
                    [(ngModel)]="row().occ.includeInSearch"
                    (ngModelChange)="includeInSearchChange.emit()"
                    class="search-checkbox"
                    [attr.aria-label]="
                        'Include in search for ' +
                        (row().occ.evStartDt | dateFormat: dateTimeFormat()) +
                        ' to ' +
                        (row().occ.evEndDt | dateFormat: dateTimeFormat())
                    "
                />
            </td>
        }
        <td attr.data-label="Dates">
            @if (showDateEditor()) {
                <s25-ng-editable-start-end-datetime
                    #datePicker
                    [startDatetime]="row().occ.evStartDt"
                    [endDatetime]="row().occ.evEndDt"
                    [noStartTime]="true"
                    [noEndTime]="true"
                    [noTimeUpdated]="true"
                    [alwaysSpansMidnight]="eventForm.model().spansMidnight"
                    [noSpansMidnightEdit]="true"
                    (startDatetimeChange)="onDateFieldsChange()"
                    (endDatetimeChange)="onDateFieldsChange()"
                />
            } @else {
                <div>
                    {{ row().occ.evStartDt | dateFormat: dateFormat() }}
                    @if (row().occ.spansMidnight) {
                        <span> - {{ row().occ.evEndDt | dateFormat: dateFormat() }}</span>
                    }
                </div>
            }
        </td>
        <td attr.data-label="Times">
            <s25-ng-editable-start-end-datetime
                #timePicker
                [startDatetime]="row().occ.evStartDt"
                [endDatetime]="row().occ.evEndDt"
                [noStartDate]="true"
                [noEndDate]="true"
                (startDatetimeChange)="onTimeFieldsChange()"
                (endDatetimeChange)="onTimeFieldsChange()"
                [class.shrink-time-picker]="shrinkTimePicker()"
            />
        </td>
        @if (extraTimesExist()) {
            <td attr.data-label="Extra Times">
                @if (showExtraTimes()) {
                    <div class="extra-times-wrapper">
                        @if (row().setupDate) {
                            <p class="c-margin-bottom--quarter">
                                <span class="ngUnderline">Setup Time</span> -
                                {{ row().setupDate | dateFormat: "MMM d, h:mm a" }}
                            </p>
                        }
                        @if (row().preDate) {
                            <p class="c-margin-bottom--quarter">
                                <span class="ngUnderline">Pre-Event Time</span> -
                                {{ row().preDate | dateFormat: "MMM d, h:mm a" }}
                            </p>
                        }
                        @if (row().postDate) {
                            <p class="c-margin-bottom--quarter">
                                <span class="ngUnderline">Post-Event Time</span> -
                                {{ row().postDate | dateFormat: "MMM d, h:mm a" }}
                            </p>
                        }
                        @if (row().takedownDate) {
                            <p class="c-margin-bottom--quarter">
                                <span class="ngUnderline">Takedown Time</span> -
                                {{ row().takedownDate | dateFormat: "MMM d, h:mm a" }}
                            </p>
                        }
                    </div>
                }
            </td>
        }
        <td attr.data-label="Comment">
            <input
                type="text"
                [(ngModel)]="row().occ.comment"
                (ngModelChange)="eventForm.bump()"
                class="c-input"
                aria-label="Comment"
            />
        </td>
        <td attr.data-label="State">
            <select
                [(ngModel)]="row().occ.state"
                [compareWith]="compareStatesById"
                (ngModelChange)="stateChange.emit($event)"
                class="cn-form__control"
                aria-label="State"
            >
                @for (state of row().states; track state.itemId) {
                    <option [ngValue]="state">{{ state.itemName }}</option>
                }
            </select>
        </td>
        @if (locationsExist()) {
            <td aria-label="Locations" class="all-date-occ-objects" attr.data-label="Locations">
                @if (row().locations.length <= 5) {
                    @for (location of row().locations; track location.itemId) {
                        <div class="all-date-occ-objects--object">
                            <button type="button" (click)="removeLocation.emit(location)">
                                <s25-ng-icon-button [type]="'close'" [ariaLabel]="'Remove'" />
                            </button>
                            <span class="icon-space ngMinImgDim">{{ location.itemName }}</span>
                        </div>
                    }
                } @else {
                    <s25-simple-collapse
                        class="date-occ-objects-collapse"
                        [expanded]="false"
                        [header]="'View Locations'"
                    >
                        <div class="collapse-content">
                            @for (location of row().locations; track location.itemId) {
                                <div class="all-date-occ-objects--object">
                                    <button type="button" (click)="removeLocation.emit(location)">
                                        <s25-ng-icon-button [type]="'close'" [ariaLabel]="'Remove'" />
                                    </button>
                                    <span class="icon-space ngMinImgDim">{{ location.itemName }}</span>
                                </div>
                            }
                        </div>
                    </s25-simple-collapse>
                }
            </td>
        }
        @if (resourcesExist()) {
            <td aria-label="Resources" class="all-date-occ-objects" attr.data-label="Resources">
                @if (row().resources.length <= 5) {
                    @for (resource of row().resources; track resource.itemId) {
                        <div class="all-date-occ-objects--object">
                            <button type="button" (click)="removeResource.emit(resource)">
                                <s25-ng-icon-button [type]="'close'" [ariaLabel]="'Remove'" />
                            </button>
                            <span class="icon-resource ngMinImgDim">{{ resource.itemName }}</span>
                        </div>
                    }
                } @else {
                    <s25-simple-collapse
                        class="date-occ-objects-collapse"
                        [expanded]="false"
                        [header]="'View Resources'"
                    >
                        <div class="collapse-content">
                            @for (resource of row().resources; track resource.itemId) {
                                <div class="all-date-occ-objects--object">
                                    <button type="button" (click)="removeResource.emit(resource)">
                                        <s25-ng-icon-button [type]="'close'" [ariaLabel]="'Remove'" />
                                    </button>
                                    <span class="icon-resource ngMinImgDim">{{ resource.itemName }}</span>
                                </div>
                            }
                        </div>
                    </s25-simple-collapse>
                }
            </td>
        }
        <td attr.data-label="Remove">
            <button type="button" data-action="remove-occ" class="aw-button aw-button--danger--outline">Remove</button>
        </td>
    `,
})
export class EventFormDateOccurrenceTableRowComponent {
    readonly row = input.required<DateOccurrenceRow>();
    readonly dateFormat = input.required<string>();
    readonly dateTimeFormat = input.required<string>();
    readonly showDateEditor = input(false);
    readonly justAdded = input(false);
    readonly extraTimesExist = input(false);
    readonly locationsExist = input(false);
    readonly resourcesExist = input(false);
    readonly shrinkTimePicker = input(false);
    readonly showExtraTimes = input(true);

    readonly includeInSearchChange = output<void>();
    readonly stateChange = output<S25ItemI>();
    readonly removeLocation = output<S25ItemI>();
    readonly removeResource = output<S25ItemI>();
    readonly dateFieldsChange = output<void>();
    readonly timeFieldsChange = output<void>();

    readonly eventForm = inject(EventFormStore);
    private readonly timePicker = viewChild<S25EditableStartEndDateTimeComponent>("timePicker");

    compareStatesById = (left: S25ItemI, right: S25ItemI): boolean => left?.itemId === right?.itemId;

    @Bind
    onDateFieldsChange(): void {
        const picker = this.timePicker();
        const formModel = this.eventForm.model();
        if (picker) {
            // Date-only edits must not overwrite occurrence times (datepicker values are midnight).
            // Re-sync the time picker from the shared occurrence datetimes, matching AngJS postDateUpdate.
            if (formModel.spansMidnight) {
                picker.resetEnd();
            }
            picker.resetStart();
        }
        this.dateFieldsChange.emit();
    }

    @Bind
    onTimeFieldsChange(): void {
        this.timeFieldsChange.emit();
    }
}
